import { useNavigate } from '@tanstack/react-router';
import type { SessionRow, EhrRow, ImportRow } from '../../lib/api.js';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

const STAGE_ORDER = ['scrape', 'parse', 'canonicalize', 'export', 'verify'];

function ProgressArc({ pct }: { pct: number }) {
  const r = 20;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(pct, 1));
  return (
    <svg width="52" height="52" className="shrink-0 -rotate-90">
      <circle cx="26" cy="26" r={r} fill="none" stroke="rgba(13,207,184,0.15)" strokeWidth="4" />
      <circle
        cx="26" cy="26" r={r} fill="none"
        stroke="#0dcfb8" strokeWidth="4"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}
      />
      <text
        x="26" y="26"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="9"
        fill="var(--text-primary)"
        style={{ transform: 'rotate(90deg)', transformOrigin: '26px 26px' }}
      >
        {Math.round(pct * 100)}%
      </text>
    </svg>
  );
}

function SessionCard({ session, ehrs }: { session: SessionRow; ehrs: EhrRow[] }) {
  const navigate = useNavigate();

  const { data: importsData } = useQuery({
    queryKey: ['session-imports', session.id],
    queryFn: () => api.getSessionImports(session.id),
    refetchInterval: 10_000,
    enabled: session.status === 'running',
  });

  const imports: ImportRow[] = importsData?.imports ?? [];
  const totalFetched = imports.reduce((s, i) => s + i.totalFetched, 0);
  const totalExpected = imports.reduce((s, i) => s + (i.totalExpected ?? 0), 0);
  const pct = totalExpected > 0 ? totalFetched / totalExpected : 0;

  const ehrSource = session.sources.find((s) => s.kind === 'ehr');
  const ehr = ehrs.find((e) => e.id === ehrSource?.ehrId || e.slug === ehrSource?.ehrSlug);

  const elapsed = session.startedAt
    ? Math.round((Date.now() - new Date(session.startedAt).getTime()) / 60000)
    : null;

  let eta: string | null = null;
  if (elapsed && elapsed > 0 && pct > 0.01 && pct < 1) {
    const remainMin = Math.round((elapsed / pct) * (1 - pct));
    eta = remainMin < 60 ? `~${remainMin}m left` : `~${Math.round(remainMin / 60)}h left`;
  }

  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/sessions/$id', params: { id: session.id } })}
      className="flex items-center gap-4 rounded-xl px-4 py-3 text-left w-full transition-colors hover:bg-bg-tertiary"
      style={{ border: '1px solid var(--glass-border)' }}
    >
      <ProgressArc pct={pct} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-text-primary truncate">
          {session.title ?? session.id.slice(0, 8)}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {ehr && (
            <span className="text-[10px] font-mono text-text-secondary">{ehr.displayName}</span>
          )}
          <span
            className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
            style={{ background: 'rgba(13,207,184,0.12)', color: 'var(--accent-teal)', border: '1px solid rgba(13,207,184,0.25)' }}
          >
            running
          </span>
          {elapsed !== null && (
            <span className="text-[10px] text-text-dimmed">{elapsed}m elapsed</span>
          )}
          {eta && (
            <span className="text-[10px]" style={{ color: 'var(--accent-blue)' }}>{eta}</span>
          )}
        </div>
        <div className="mt-1.5 flex gap-1">
          {STAGE_ORDER.map((stage) => (
            <div
              key={stage}
              className="h-1 flex-1 rounded-full"
              style={{ background: 'rgba(13,207,184,0.15)' }}
            />
          ))}
        </div>
      </div>
      <div className="text-right text-xs text-text-dimmed shrink-0">
        <div className="font-mono">{totalFetched.toLocaleString()}</div>
        {totalExpected > 0 && (
          <div className="text-[10px] opacity-60">/ {totalExpected.toLocaleString()}</div>
        )}
      </div>
    </button>
  );
}

export function RunningSessionCards({ sessions, ehrs }: { sessions: SessionRow[]; ehrs: EhrRow[] }) {
  const running = sessions
    .filter((s) => s.status === 'running')
    .sort((a, b) => (a.startedAt ?? '').localeCompare(b.startedAt ?? ''));

  if (running.length === 0) {
    return (
      <div className="text-sm text-text-dimmed py-4 text-center">No sessions currently running</div>
    );
  }

  return (
    <div className="space-y-2">
      {running.map((s) => (
        <SessionCard key={s.id} session={s} ehrs={ehrs} />
      ))}
    </div>
  );
}
